import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { TurmaFormDialog } from "@/components/forms/TurmaFormDialog";
import { AlunoFormDialog } from "@/components/forms/AlunoFormDialog";
import { InscricaoManager } from "@/components/InscricaoManager";
import { BancoCapacitados } from "@/components/BancoCapacitados";
import { TipoBadge } from "@/components/TipoBadge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, Loader2, Pencil, Plus, UserPlus, Users, Dumbbell, ClipboardCheck, BarChart3, ListChecks, Search, Check, Eye, Clock, User, X, Lock, Unlock, ShieldCheck, AlertTriangle, MoreVertical, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { playFinalize } from "@/lib/sound";

const STATUS_BADGE = {
  ativa: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  planejada: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  encerrada: "bg-slate-500/20 text-slate-300 border-slate-500/40",
  finalizada: "bg-amber-500/20 text-amber-300 border-amber-500/40",
};

export default function TurmaDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [turma, setTurma] = useState(null);
  const [alunos, setAlunos] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [instrutoresAtivos, setInstrutoresAtivos] = useState([]);
  const [addInstOpen, setAddInstOpen] = useState(false);
  const [novoMonitor, setNovoMonitor] = useState("");
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [novoAlunoOpen, setNovoAlunoOpen] = useState(false);
  const [existOpen, setExistOpen] = useState(false);
  const [todos, setTodos] = useState([]);
  const [busca, setBusca] = useState("");
  const [sel, setSel] = useState([]);
  const [selOficina, setSelOficina] = useState("");
  const [starting, setStarting] = useState(null);
  const [oficinasDialog, setOficinasDialog] = useState(false);
  const [selOfIds, setSelOfIds] = useState([]);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [confirmFinOpen, setConfirmFinOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [finBusy, setFinBusy] = useState(false);
  const [motivoReabrir, setMotivoReabrir] = useState("");
  const [editAluno, setEditAluno] = useState(null);
  const [removeAluno, setRemoveAluno] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setErro(false);
    api.get(`/turmas/${id}`).then(async (r) => {
      const t = r.data;
      setTurma(t);
      const [al, of, av] = await Promise.all([
        api.get(`/alunos?turma_id=${id}`),
        t.curso_id ? api.get(`/oficinas?curso_id=${t.curso_id}`) : Promise.resolve({ data: [] }),
        api.get(`/avaliacoes?turma_id=${id}`),
      ]);
      setAlunos(al.data); setOficinas(of.data); setAvaliacoes(av.data);
    }).catch(() => setErro(true)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/auth/instrutores/ativos").then((r) => setInstrutoresAtivos(r.data)).catch(() => setInstrutoresAtivos([])); }, []);

  const jaNaTurma = (a) => a.turma_id === id || (a.turma_ids || []).includes(id);

  const openExistentes = async () => {
    setSel([]); setBusca(""); setExistOpen(true);
    const { data } = await api.get("/alunos");
    setTodos(data);
  };
  const toggleSel = (aid) => setSel((s) => s.includes(aid) ? s.filter((x) => x !== aid) : [...s, aid]);
  const vincularSelecionados = async () => {
    let ok = 0;
    for (const aid of sel) {
      try { await api.post(`/turmas/${id}/alunos/${aid}`); ok += 1; }
      catch (e) { /* já vinculado ou sem permissão */ }
    }
    toast.success(`${ok} aluno(s) adicionado(s) à turma`);
    setExistOpen(false); setSel([]);
    load();
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (erro || !turma) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">Turma não encontrada ou indisponível.</p>
      <button data-testid="voltar-btn" onClick={() => navigate("/turmas")} className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Voltar para Turmas
      </button>
    </div>
  );

  const bt = busca.toLowerCase();
  const filtrados = todos.filter((a) => !bt || [a.nome, a.nome_guerra, a.rg, a.matricula, a.identificacao_funcional, a.instituicao_nome, a.unidade_nome].some((f) => (f || "").toLowerCase().includes(bt)));

  // Oficinas efetivamente utilizadas por esta turma (associação turma↔oficinas).
  // oficina_ids null (legado/curso não-PI) => usa todo o catálogo do curso (comportamento atual).
  const isPI = turma.banco_criterios === "primeiro_interventor";
  const isFinalizada = !!turma.finalizada;
  const canFinalize = isAdmin || turma.responsavel_id === user?.id;
  const selIds = turma.oficina_ids;
  // Contexto da turma: quando oficina_ids não está definido (turmas PI legadas), não exibir o
  // catálogo inteiro do curso (compartilhado entre turmas). Mostrar apenas oficinas com atividade
  // REAL nesta turma, derivadas das avaliações filtradas por turma_id. Não remove vínculos nem altera dados.
  const ofIdsComAtividade = new Set(avaliacoes.map((a) => a.oficina_id));
  const oficinasTurma = Array.isArray(selIds)
    ? oficinas.filter((o) => selIds.includes(o.id))
    : (isPI ? oficinas.filter((o) => ofIdsComAtividade.has(o.id)) : oficinas);

  // Contexto operacional: oficina em que os alunos serão avaliados (dentro da turma)
  const ofId = selOficina || oficinasTurma[0]?.id || "";
  const back = encodeURIComponent(`/turmas/${id}`);
  const avPorAluno = {};
  for (const av of avaliacoes) {
    if (av.oficina_id !== ofId) continue;
    const cur = avPorAluno[av.aluno_id];
    if (!cur || (av.status === "concluida" && cur.status !== "concluida")) avPorAluno[av.aluno_id] = av;
  }
  const nomeExib = (a) => `${a.grau_nome && a.grau_nome !== "Não se aplica" ? a.grau_nome + " " : ""}${a.nome_guerra || a.nome}`;
  const iniciar = async (a) => {
    if (isFinalizada) { toast.error("Turma finalizada: avaliações bloqueadas. Reabra a turma (Administrador) para editar."); return; }
    if (!ofId) { toast.error("Cadastre/selecione uma oficina para avaliar."); return; }
    setStarting(a.id);
    try {
      const existente = avaliacoes.find((x) => x.aluno_id === a.id && x.oficina_id === ofId && x.status === "em_andamento");
      if (existente) { navigate(`/avaliar/${existente.id}?back=${back}`); return; }
      const { data } = await api.post("/avaliacoes", { curso_id: turma.curso_id, turma_id: id, oficina_id: ofId, aluno_id: a.id });
      navigate(`/avaliar/${data.id}?back=${back}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setStarting(null); }
  };
  const acaoPrincipal = (a) => {
    const av = avPorAluno[a.id];
    if (av?.status === "concluida") { navigate(`/avaliacoes/${av.id}?back=${back}`); return; }
    if (av?.status === "em_andamento") { navigate(`/avaliar/${av.id}?back=${back}`); return; }
    iniciar(a);
  };

  // ---- Equipe (Instrutores + Monitores) gerenciável na própria tela da turma ----
  const canManageEquipe = (isAdmin || (turma.instrutor_ids || []).includes(user?.id) || turma.responsavel_id === user?.id) && !isFinalizada;
  const equipeIds = turma.instrutor_ids || [];
  const equipeMon = turma.monitores || [];
  const salvarEquipe = async (ids, mons) => {
    try { await api.put(`/turmas/${id}/equipe`, { instrutor_ids: ids, monitores: mons }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addInstrutorEquipe = (iid) => { setAddInstOpen(false); if (!equipeIds.includes(iid)) salvarEquipe([...equipeIds, iid], equipeMon); };
  const removeInstrutorEquipe = (iid) => salvarEquipe(equipeIds.filter((x) => x !== iid), equipeMon);
  const addMonitorEquipe = () => {
    const v = (novoMonitor || "").trim();
    if (!v) return;
    if (equipeMon.some((m) => m.toLowerCase() === v.toLowerCase())) { toast.error("Monitor já adicionado"); return; }
    salvarEquipe(equipeIds, [...equipeMon, v]); setNovoMonitor("");
  };
  const editMonitorEquipe = (idx) => { setNovoMonitor(equipeMon[idx]); salvarEquipe(equipeIds, equipeMon.filter((_, i) => i !== idx)); };
  const removeMonitorEquipe = (idx) => salvarEquipe(equipeIds, equipeMon.filter((_, i) => i !== idx));

  // ---- Oficinas da turma (associação) ----
  const openOficinasDialog = () => { setSelOfIds(oficinasTurma.map((o) => o.id)); setOficinasDialog(true); };
  const toggleOf = (oid) => setSelOfIds((s) => s.includes(oid) ? s.filter((x) => x !== oid) : [...s, oid]);
  const salvarOficinas = async () => {
    try {
      await api.put(`/turmas/${id}/oficinas`, { oficina_ids: selOfIds });
      toast.success("Oficinas da turma atualizadas");
      setOficinasDialog(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- Finalizar / Reabrir instrução (PI) ----
  const abrirFinalizar = async () => {
    setPreview(null); setFinalizeOpen(true);
    try { const { data } = await api.get(`/turmas/${id}/finalizacao-preview`); setPreview(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setFinalizeOpen(false); }
  };
  const irParaConfirmar = () => { setFinalizeOpen(false); setConfirmFinOpen(true); };
  const doFinalizar = async () => {
    setFinBusy(true);
    try {
      await api.post(`/turmas/${id}/finalizar`);
      // Som ESPECIAL de finalização (substitui o som comum de sucesso — apenas um efeito de conclusão).
      playFinalize();
      toast("Instrução finalizada e salva.");
      setConfirmFinOpen(false); load();
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setFinBusy(false); }
  };
  const doReabrir = async () => {
    const motivo = (motivoReabrir || "").trim();
    if (!motivo) { toast.error("Informe o motivo da reabertura."); return; }
    setFinBusy(true);
    try { await api.post(`/turmas/${id}/reabrir`, { motivo }); toast.success("Turma reaberta."); setReopenOpen(false); setMotivoReabrir(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setFinBusy(false); }
  };

  // ---- Editar / Remover aluno da turma ----
  const canEditAlunos = (isAdmin || (turma.instrutor_ids || []).includes(user?.id) || turma.responsavel_id === user?.id) && !isFinalizada;
  const alunoTemAvaliacao = (aid) => avaliacoes.some((x) => x.aluno_id === aid);
  const doRemoverAlunoTurma = async () => {
    if (!removeAluno) return;
    setRemoveBusy(true);
    try {
      await api.delete(`/turmas/${id}/alunos/${removeAluno.id}`);
      toast.success("Aluno removido desta turma. O cadastro foi preservado.");
      setRemoveAluno(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRemoveBusy(false); }
  };

  return (
    <div data-testid="turma-detalhe">
      <button data-testid="voltar-btn" onClick={() => navigate(turma.curso_id ? (turma.curso_tipo === "instrucao" ? `/instrucoes/${turma.curso_id}` : `/cursos/${turma.curso_id}`) : "/turmas")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> {turma.curso_nome || "Turmas"}
      </button>
      <PageHeader title={turma.nome} subtitle={turma.curso_nome}>
        {isAdmin && <Button variant="secondary" data-testid="editar-turma-btn" onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4 mr-2" />Editar</Button>}
      </PageHeader>

      <Card className="p-5 bg-card border-border mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <TipoBadge tipo={turma.curso_tipo || turma.curso?.tipo} />
          <Badge className={STATUS_BADGE[turma.status] || "bg-secondary text-secondary-foreground border-border"}>{turma.status}</Badge>
          {turma.data_inicio && <span className="text-xs text-muted-foreground font-mono">{turma.data_inicio} → {turma.data_termino || "?"}</span>}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4 text-sm">
          {turma.instituicao && <Info label="Instituição" value={turma.instituicao} />}
          {turma.unidade && <Info label="Unidade / Organização" value={turma.unidade} />}
          {turma.local && <Info label="Local" value={turma.local} />}
          {turma.responsavel && <Info label="Responsável" value={turma.responsavel} />}
        </div>
        {/* INSTRUTORES — gerenciável na tela da turma */}
        <div className="mt-4" data-testid="turma-instrutores">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-1">Instrutores</div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {(turma.instrutores || []).length ? turma.instrutores.map((i) => (
              <Badge key={i.id} data-testid={`instrutor-chip-${i.id}`} className="bg-primary/15 text-primary border-primary/30 flex items-center gap-1">
                {i.nome}
                {canManageEquipe && <button type="button" data-testid={`remover-instrutor-${i.id}`} onClick={() => removeInstrutorEquipe(i.id)} className="ml-0.5 hover:text-red-400"><X className="h-3 w-3" /></button>}
              </Badge>
            )) : <span className="text-xs text-muted-foreground">Nenhum instrutor.</span>}
          </div>
          {canManageEquipe && (
            <Popover open={addInstOpen} onOpenChange={setAddInstOpen}>
              <PopoverTrigger asChild>
                <Button size="sm" variant="secondary" data-testid="add-instrutor-btn" className="mt-2"><Plus className="h-4 w-4 mr-1.5" />Adicionar instrutor</Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 bg-popover border-border w-[280px] max-w-[90vw]" align="start">
                <Command>
                  <CommandInput placeholder="Buscar instrutor..." data-testid="add-instrutor-search" />
                  <CommandList>
                    <CommandEmpty>Nenhum instrutor disponível.</CommandEmpty>
                    <CommandGroup>
                      {instrutoresAtivos.filter((i) => !equipeIds.includes(i.id)).map((i) => (
                        <CommandItem key={i.id} value={i.display} data-testid={`add-instrutor-opt-${i.id}`} onSelect={() => addInstrutorEquipe(i.id)}>
                          <Plus className="mr-2 h-4 w-4" />{i.display}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>

        {/* MONITORES — digitação manual, gerenciável na tela da turma */}
        <div className="mt-4" data-testid="turma-monitores">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono mb-1">Monitores</div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {(turma.monitores || []).length ? turma.monitores.map((m, i) => (
              <Badge key={i} data-testid={`monitor-chip-${i}`} className="bg-secondary text-secondary-foreground border-border flex items-center gap-1">
                {m}
                {canManageEquipe && <>
                  <button type="button" data-testid={`editar-monitor-${i}`} onClick={() => editMonitorEquipe(i)} className="ml-0.5 hover:text-primary"><Pencil className="h-3 w-3" /></button>
                  <button type="button" data-testid={`remover-monitor-${i}`} onClick={() => removeMonitorEquipe(i)} className="hover:text-red-400"><X className="h-3 w-3" /></button>
                </>}
              </Badge>
            )) : <span className="text-xs text-muted-foreground">Nenhum monitor.</span>}
          </div>
          {canManageEquipe && (
            <div className="flex items-center gap-2 mt-2">
              <Input data-testid="novo-monitor-input" value={novoMonitor} onChange={(e) => setNovoMonitor(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMonitorEquipe(); } }}
                placeholder="Ex.: 3º SGT Silva" className="max-w-[240px] h-9" />
              <Button size="sm" variant="secondary" data-testid="add-monitor-btn" onClick={addMonitorEquipe}><Plus className="h-4 w-4 mr-1.5" />Adicionar monitor</Button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {!isFinalizada && <>
            <Button size="sm" data-testid="novo-aluno-turma-btn" onClick={() => setNovoAlunoOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Novo aluno</Button>
            <Button size="sm" variant="secondary" data-testid="aluno-existente-btn" onClick={openExistentes}><UserPlus className="h-4 w-4 mr-1.5" />Adicionar aluno existente</Button>
          </>}
          <Button size="sm" variant="outline" data-testid="consolidado-formativa-btn" onClick={() => navigate(`/formativa/consolidado/${id}`)}><ListChecks className="h-4 w-4 mr-1.5" />Consolidado formativo</Button>
        </div>
        <div className="mt-3"><InscricaoManager turma={turma} /></div>
      </Card>

      {isPI && (
        <Card className="p-4 bg-card border-border mb-6" data-testid="finalizacao-card">
          {isFinalizada ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Lock className="h-5 w-5 text-amber-400 shrink-0" />
                <div className="min-w-0">
                  <div className="font-heading font-bold uppercase tracking-tight text-amber-300">Instrução finalizada</div>
                  <div className="text-xs text-muted-foreground">
                    {turma.finalizacao?.finalizada_por_nome ? `Finalizada por ${turma.finalizacao.finalizada_por_nome}` : "Finalizada"}
                    {turma.finalizacao?.finalizada_em ? ` · ${new Date(turma.finalizacao.finalizada_em).toLocaleString("pt-BR")}` : ""} — dados disponíveis para consulta e relatórios; alterações bloqueadas.
                  </div>
                </div>
              </div>
              {isAdmin && <Button variant="secondary" data-testid="reabrir-turma-btn" onClick={() => { setMotivoReabrir(""); setReopenOpen(true); }}><Unlock className="h-4 w-4 mr-2" />Reabrir turma</Button>}
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck className="h-5 w-5 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="font-heading font-bold uppercase tracking-tight">Encerramento da instrução</div>
                  <div className="text-xs text-muted-foreground">Finalize a turma após concluir as atividades. Presenças, avaliações e resultados são preservados.</div>
                </div>
              </div>
              {canFinalize && <Button data-testid="finalizar-instrucao-btn" onClick={abrirFinalizar} style={{ backgroundImage: "linear-gradient(90deg, #dc2626, #9f1239)" }} className="text-white border-0 hover:opacity-90"><Lock className="h-4 w-4 mr-2" />Finalizar e salvar a instrução</Button>}
            </div>
          )}
        </Card>
      )}

      {turma.banco_criterios === "primeiro_interventor" && <BancoCapacitados turma={turma} />}

      {oficinasTurma.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-sm" data-testid="turma-oficina-contexto">
          <span className="text-muted-foreground">Avaliar na oficina:</span>
          {oficinasTurma.length === 1
            ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40"><Dumbbell className="h-3.5 w-3.5 mr-1" />{oficinasTurma[0].nome}</Badge>
            : (
              <Select value={ofId} onValueChange={setSelOficina}>
                <SelectTrigger data-testid="turma-oficina-select" className="w-auto min-w-[220px] h-8"><SelectValue placeholder="Selecione a oficina" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {oficinasTurma.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
        </div>
      )}

      <Section title={`Alunos (${alunos.length})`} icon={Users}>
        {alunos.length ? alunos.map((a) => {
          const av = avPorAluno[a.id];
          const st = av?.status;
          const acao = st === "concluida"
            ? { label: "Ver resultado", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", Icon: Eye }
            : st === "em_andamento"
              ? { label: "Retomar avaliação", cls: "bg-sky-500/20 text-sky-300 border-sky-500/40", Icon: Clock }
              : { label: ofId ? "Avaliar" : "Sem oficina", cls: "bg-primary/15 text-primary border-primary/30", Icon: ClipboardCheck };
          return (
            <div key={a.id} data-testid={`aluno-row-${a.id}`} className="w-full flex items-center gap-2 px-4 py-3 hover:bg-secondary/50 transition-colors">
              <button data-testid={`aluno-avaliar-${a.id}`} disabled={starting === a.id} onClick={() => acaoPrincipal(a)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left disabled:opacity-60">
                <div className="p-2 rounded-full bg-amber-500/15 shrink-0"><Users className="h-4 w-4 text-amber-400" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{nomeExib(a)}</div>
                  <div className="text-xs text-muted-foreground truncate">{a.nome_guerra ? a.nome : (a.grau_nome || a.matricula || a.identificacao_funcional || "—")}{a.instituicao_nome ? ` · ${a.instituicao_nome}` : ""}</div>
                </div>
                <Badge className={`${acao.cls} shrink-0`}>
                  {starting === a.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <acao.Icon className="h-3 w-3 mr-1" />}
                  {acao.label}
                </Badge>
              </button>
              <Button size="sm" variant="outline" data-testid={`aluno-ver-perfil-${a.id}`} onClick={() => navigate(`/alunos/${a.id}?back=${back}`)} className="shrink-0">
                <User className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Ver perfil</span>
              </Button>
              {canEditAlunos && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid={`aluno-acoes-${a.id}`} className="shrink-0 px-2"><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover border-border">
                    <DropdownMenuItem data-testid={`aluno-editar-${a.id}`} onClick={() => setEditAluno(a)}>
                      <Pencil className="h-4 w-4 mr-2" />Editar aluno
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid={`aluno-remover-${a.id}`} onClick={() => setRemoveAluno(a)} className="text-red-400 focus:text-red-400">
                      <Trash2 className="h-4 w-4 mr-2" />Remover da turma
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          );
        }) : <Empty>Nenhum aluno nesta turma.</Empty>}
      </Section>

      <div className="mb-6">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2"><Dumbbell className="h-5 w-5 text-primary" /><h2 className="font-heading text-xl font-bold uppercase tracking-tight">{isPI ? `Oficinas da turma (${oficinasTurma.length})` : `Oficinas do curso (${oficinasTurma.length})`}</h2></div>
          {isPI && canManageEquipe && (
            <Button size="sm" variant="secondary" data-testid="gerir-oficinas-btn" onClick={openOficinasDialog}><Plus className="h-4 w-4 mr-1.5" />Adicionar / gerir oficinas</Button>
          )}
        </div>
        <Card className="bg-card border-border divide-y divide-border">
          {oficinasTurma.length ? oficinasTurma.map((o) => (
            <button key={o.id} data-testid={`oficina-link-${o.id}`} onClick={() => navigate(`/aplicacao/${id}/${o.id}`)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors text-left">
              <div className="p-2 rounded-lg bg-emerald-500/15 shrink-0"><Dumbbell className="h-4 w-4 text-emerald-400" /></div>
              <div className="min-w-0"><div className="font-medium truncate">{o.nome}</div><div className="text-xs text-muted-foreground truncate">Avaliar participantes · {(o.criterios || []).length} critérios</div></div>
              {o.escala_labels && <Badge className="ml-auto bg-primary/15 text-primary border-primary/30 shrink-0" onClick={(e) => { e.stopPropagation(); navigate(`/pratica/consolidado/${id}/${o.id}`); }} data-testid={`consolidado-pratica-${o.id}`}><BarChart3 className="h-3.5 w-3.5" /></Badge>}
            </button>
          )) : (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {isPI ? "Nenhuma oficina selecionada para esta turma." : "Nenhuma oficina no curso."}
              {isPI && canManageEquipe && <div className="mt-3"><Button size="sm" data-testid="gerir-oficinas-empty-btn" onClick={openOficinasDialog}><Plus className="h-4 w-4 mr-1.5" />Adicionar oficina</Button></div>}
            </div>
          )}
        </Card>
      </div>

      <Section title={`Avaliações (${avaliacoes.length})`} icon={ClipboardCheck}>
        {avaliacoes.length ? avaliacoes.slice(0, 30).map((av) => (
          <button key={av.id} data-testid={`aval-link-${av.id}`} onClick={() => navigate(`/avaliacoes/${av.id}?back=${encodeURIComponent(`/turmas/${id}`)}`)}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors text-left">
            <div className="min-w-0"><div className="font-medium truncate">{av.aluno_nome} · {av.oficina_nome}</div><div className="text-xs text-muted-foreground truncate">{av.avaliador_nome} · {av.status === "concluida" ? "Concluída" : "Em andamento"}</div></div>
          </button>
        )) : <Empty>Nenhuma avaliação registrada.</Empty>}
      </Section>

      <TurmaFormDialog open={editOpen} onOpenChange={setEditOpen} turma={turma} onSaved={load} />
      <AlunoFormDialog open={novoAlunoOpen} onOpenChange={setNovoAlunoOpen} lockTurmaId={id} onSaved={load} />
      <AlunoFormDialog open={!!editAluno} onOpenChange={(v) => { if (!v) setEditAluno(null); }} aluno={editAluno} lockTurmaId={id} onSaved={load} />

      <Dialog open={!!removeAluno} onOpenChange={(v) => { if (!v) setRemoveAluno(null); }}>
        <DialogContent className="bg-popover border-border" data-testid="remover-aluno-dialog">
          <DialogHeader><DialogTitle>Deseja remover este aluno desta turma?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {removeAluno ? nomeExib(removeAluno) : ""} será retirado(a) somente desta turma. O cadastro no banco geral é preservado e poderá ser adicionado novamente no futuro. O histórico em outras turmas não é afetado.
          </p>
          {removeAluno && alunoTemAvaliacao(removeAluno.id) && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="remover-aluno-aviso-avaliacao">
              <div className="flex items-center gap-2 font-medium mb-1"><AlertTriangle className="h-4 w-4" />Existem dados vinculados</div>
              <div className="text-xs">Este aluno possui avaliação(ões) nesta turma. Para preservar o histórico, a remoção está bloqueada. A exclusão de avaliações é ação administrativa separada e protegida.</div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="remover-aluno-cancelar" onClick={() => setRemoveAluno(null)}>Cancelar</Button>
            <Button
              data-testid="remover-aluno-confirmar"
              disabled={removeBusy || (removeAluno && alunoTemAvaliacao(removeAluno.id))}
              onClick={doRemoverAlunoTurma}
              className="bg-red-600 hover:bg-red-700 text-white">
              {removeBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}Remover da turma
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={oficinasDialog} onOpenChange={setOficinasDialog}>
        <DialogContent className="bg-popover border-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Oficinas da turma</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Selecione quais oficinas da instrução esta turma utilizará. As oficinas continuam no catálogo compartilhado — o mesmo aluno da turma é usado em todas elas. Não é obrigatório o aluno passar por todas.</p>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {oficinas.length ? oficinas.map((o) => {
              const checked = selOfIds.includes(o.id);
              return (
                <label key={o.id} data-testid={`gerir-oficina-${o.id}`} className={`w-full flex items-center gap-2.5 border rounded-md p-2.5 cursor-pointer ${checked ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}>
                  <Checkbox checked={checked} onCheckedChange={() => toggleOf(o.id)} data-testid={`gerir-oficina-check-${o.id}`} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{o.nome}</div>
                    <div className="text-xs text-muted-foreground truncate">{(o.criterios || []).length} critérios</div>
                  </div>
                </label>
              );
            }) : <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma oficina cadastrada no catálogo da instrução ainda.</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="salvar-oficinas-btn" onClick={salvarOficinas}>Salvar seleção</Button>
            <Button variant="outline" data-testid="criar-oficina-btn" onClick={() => navigate(`/oficinas?curso_id=${turma.curso_id}`)}><Plus className="h-4 w-4 mr-1.5" />Criar nova oficina</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={existOpen} onOpenChange={setExistOpen}>
        <DialogContent className="bg-popover border-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Adicionar aluno existente</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Selecione um ou mais alunos já cadastrados para vincular a esta turma (o cadastro original é preservado; o mesmo aluno pode estar em várias turmas).</p>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input data-testid="busca-aluno-existente" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome, nome de guerra, RG, matrícula, instituição, unidade..." className="pl-8" />
          </div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {filtrados.map((a) => {
              const linked = jaNaTurma(a);
              const checked = sel.includes(a.id);
              return (
                <button key={a.id} data-testid={`vincular-aluno-${a.id}`} disabled={linked}
                  onClick={() => toggleSel(a.id)}
                  className={`w-full flex items-center justify-between gap-2 border rounded-md p-2.5 text-left ${linked ? "opacity-50 cursor-not-allowed border-border" : checked ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{a.nome}{a.nome_guerra ? <span className="text-xs text-primary font-mono ml-1">"{a.nome_guerra}"</span> : ""}</div>
                    <div className="text-xs text-muted-foreground truncate">{[a.matricula, a.instituicao_nome, a.unidade_nome].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  {linked
                    ? <span className="text-xs text-emerald-400 shrink-0 flex items-center gap-1"><Check className="h-3.5 w-3.5" />Já adicionado</span>
                    : checked ? <Check className="h-4 w-4 text-primary shrink-0" /> : <Plus className="h-4 w-4 text-muted-foreground shrink-0" />}
                </button>
              );
            })}
            {!filtrados.length && <p className="text-sm text-muted-foreground py-4 text-center">Nenhum aluno encontrado.</p>}
          </div>
          <Button data-testid="adicionar-selecionados-btn" onClick={vincularSelecionados} disabled={!sel.length}>
            Adicionar {sel.length || ""} selecionado(s) à turma
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent className="bg-popover border-border" data-testid="finalizar-preview-dialog">
          <DialogHeader><DialogTitle>Conferência antes de finalizar</DialogTitle></DialogHeader>
          {!preview ? <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Carregando dados da turma…</div> : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Info label="Total de alunos" value={preview.total_alunos} />
                <Info label="Presenças confirmadas" value={preview.presencas_confirmadas} />
                <Info label="Ausências registradas" value={preview.ausencias} />
                <Info label="Avaliados" value={preview.avaliados} />
                <Info label="Não avaliados" value={preview.nao_avaliados} />
                <Info label="Avaliações em andamento" value={preview.avaliacoes_em_andamento} />
                <Info label="Travas colaborativas ativas" value={preview.travas_ativas} />
              </div>
              {preview.bloqueios?.length > 0 ? (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="finalizar-bloqueios">
                  <div className="flex items-center gap-2 font-medium mb-1"><AlertTriangle className="h-4 w-4" />Não é possível finalizar agora</div>
                  <ul className="list-disc pl-5 text-xs">{preview.bloqueios.map((b, i) => <li key={i}>{b}</li>)}</ul>
                  <div className="text-xs mt-1">Conclua com segurança as avaliações em andamento (as travas não são liberadas automaticamente) e tente novamente.</div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Alunos não avaliados permanecem sem nota e sem classificação de aptidão — isso não impede o encerramento.</p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" data-testid="finalizar-cancelar-1" onClick={() => setFinalizeOpen(false)}>Cancelar</Button>
                <Button data-testid="finalizar-prosseguir" disabled={!preview.pode_finalizar} onClick={irParaConfirmar}>Prosseguir</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmFinOpen} onOpenChange={setConfirmFinOpen}>
        <DialogContent className="bg-popover border-border" data-testid="finalizar-confirm-dialog">
          <DialogHeader><DialogTitle>Confirma a finalização desta instrução?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">A turma será encerrada e seus dados permanecerão disponíveis para consulta e geração de relatórios. Após a finalização, somente o Administrador e o Responsável pela turma poderão reabrir a turma.</p>
          <div className="flex justify-end gap-2" data-sound="off">
            <Button variant="outline" data-testid="finalizar-cancelar-2" onClick={() => setConfirmFinOpen(false)}>Cancelar</Button>
            <Button data-testid="finalizar-confirmar" disabled={finBusy} onClick={doFinalizar} style={{ backgroundImage: "linear-gradient(90deg, #dc2626, #9f1239)" }} className="text-white border-0 hover:opacity-90">{finBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}Finalizar e salvar a instrução</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="bg-popover border-border" data-testid="reabrir-dialog">
          <DialogHeader><DialogTitle>Reabrir turma finalizada</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Somente o Administrador pode reabrir. Informe o motivo (registrado em auditoria). Após reabrir, a turma volta ao funcionamento operacional.</p>
          <Input data-testid="reabrir-motivo" value={motivoReabrir} onChange={(e) => setMotivoReabrir(e.target.value)} placeholder="Motivo da reabertura" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="reabrir-cancelar" onClick={() => setReopenOpen(false)}>Cancelar</Button>
            <Button data-testid="reabrir-confirmar" disabled={finBusy} onClick={doReabrir}>{finBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlock className="h-4 w-4 mr-2" />}Reabrir turma</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Info({ label, value }) {
  return <div><div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">{label}</div><div className="font-medium">{value}</div></div>;
}
function Section({ title, icon: Icon, children }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3"><Icon className="h-5 w-5 text-primary" /><h2 className="font-heading text-xl font-bold uppercase tracking-tight">{title}</h2></div>
      <Card className="bg-card border-border divide-y divide-border">{children}</Card>
    </div>
  );
}
function Empty({ children }) {
  return <div className="px-4 py-8 text-center text-sm text-muted-foreground">{children}</div>;
}
